"use client";

import React from "react";
import { ArrowRight, FileText, CheckCircle2, Cog, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface StepItem {
  num: string;
  title: string;
  desc: string;
}
interface HowWeWorkContent {
  eyebrow: string;
  heading: string;
  lead: string;
  steps: StepItem[];
  primaryBtn: string;
  secondaryBtn: string;
}
const copy: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", HowWeWorkContent> = {
  en: {
    eyebrow: "How We Work",
    heading: "How We Support Your Project",
    lead: "From first enquiry to supply coordination, we work from your specification.",
    steps: [{
      num: "01",
      title: "Define the requirement",
      desc: "Share your application, mesh type, aperture or micron target, material and quantity."
    }, {
      num: "02",
      title: "Match the specification",
      desc: "We match your requirement to a woven, sintered, component or wedge wire product within public standards (ISO 9044 / ASTM E2016)."
    }, {
      num: "03",
      title: "Engineer custom parts",
      desc: "Custom sizes, shapes, drawings and OEM components where the standard range does not fit."
    }, {
      num: "04",
      title: "Quote and coordinate supply",
      desc: "A written quotation and coordinated supply for international shipments."
    }],
    primaryBtn: "Request a Quote",
    secondaryBtn: "OEM Custom Engineering"
  },
  zh: {
    eyebrow: "工作流程",
    heading: "我们如何支持您的项目",
    lead: "从初步咨询到供应协调，我们严格依照您的规格要求推进。",
    steps: [{
      num: "01",
      title: "明确需求",
      desc: "告知您的应用场景、丝网类型、孔径或微米精度目标、材质及采购数量。"
    }, {
      num: "02",
      title: "匹配规格",
      desc: "在公共标准（ISO 9044 / ASTM E2016）范围内，将您的需求精确匹配至编织网、烧结网、过滤组件或楔形丝产品。"
    }, {
      num: "03",
      title: "定制工程部件",
      desc: "针对标准规格不适用的场景，提供定制尺寸、形状、图纸加工与 OEM 组件工程支持。"
    }, {
      num: "04",
      title: "报价与供应协调",
      desc: "出具正式书面报价，并为国际运输与交付提供全程协调保障。"
    }],
    primaryBtn: "获取报价",
    secondaryBtn: "OEM 定制工程"
  },
  es: {
    eyebrow: "Cómo trabajamos",
    heading: "Cómo apoyamos su proyecto",
    lead: "Desde la primera consulta hasta la coordinación del suministro, trabajamos a partir de sus especificaciones.",
    steps: [{
      num: "01",
      title: "Definir el requisito",
      desc: "Comparta su aplicación, tipo de malla, abertura o micraje objetivo, material y cantidad."
    }, {
      num: "02",
      title: "Ajustar la especificación",
      desc: "Adaptamos su requisito a una solución tejida, sinterizada, componente o alambre en cuña bajo normas públicas (ISO 9044 / ASTM E2016)."
    }, {
      num: "03",
      title: "Ingeniería de piezas a medida",
      desc: "Medidas especiales, formas, planos y componentes OEM cuando la gama estándar no se ajusta."
    }, {
      num: "04",
      title: "Cotización y coordinación del suministro",
      desc: "Presupuesto formal por escrito y coordinación de envíos internacionales."
    }],
    primaryBtn: "Solicitar presupuesto",
    secondaryBtn: "Ingeniería OEM a medida"
  },
  de: {
    eyebrow: "Unsere Arbeitsweise",
    heading: "Wie wir Ihr Projekt unterstützen",
    lead: "Von der ersten Anfrage bis zur Lieferkoordination arbeiten wir exakt nach Ihrer Spezifikation.",
    steps: [{
      num: "01",
      title: "Anforderungen definieren",
      desc: "Nennen Sie Einsatzbereich, Gewebetyp, Maschenweite bzw. Mikronwert, Werkstoff und Menge."
    }, {
      num: "02",
      title: "Spezifikation abgleichen",
      desc: "Wir gleichen Ihren Bedarf mit Drahtgewebe, Sintergewebe, Filterkomponenten oder Spaltsieben nach Normen (ISO 9044 / ASTM E2016) ab."
    }, {
      num: "03",
      title: "Sonderteile fertigen",
      desc: "Individuelle Abmessungen, Zuschnitte, Zeichnungsteile und OEM-Komponenten, wenn Standardreihen nicht genügen."
    }, {
      num: "04",
      title: "Angebot & Lieferkoordination",
      desc: "Schriftliches Angebot und abgestimmte Logistik für internationale Lieferungen."
    }],
    primaryBtn: "Angebot anfragen",
    secondaryBtn: "OEM-Sonderfertigung"
  },
  fr: {
    eyebrow: "Notre méthode",
    heading: "Comment nous accompagnons votre projet",
    lead: "De la première prise de contact à la coordination logistique, nous travaillons selon vos spécifications.",
    steps: [{
      num: "01",
      title: "Définir le besoin",
      desc: "Précisez votre application, type de toile, ouverture ou seuil de filtration, nuance d'acier et quantité."
    }, {
      num: "02",
      title: "Valider la spécification",
      desc: "Nous faisons correspondre votre demande à nos toiles tissées, frittées, composants ou profilés selon les normes (ISO 9044 / ASTM E2016)."
    }, {
      num: "03",
      title: "Concevoir des pièces sur mesure",
      desc: "Dimensions, découpes, plans spécifiques et composants OEM lorsque les gammes standard ne conviennent pas."
    }, {
      num: "04",
      title: "Devis et coordination logistique",
      desc: "Offre de prix formelle et prise en charge coordonnée pour vos expéditions internationales."
    }],
    primaryBtn: "Demander un devis",
    secondaryBtn: "Ingénierie OEM sur mesure"
  },
  pt: {
    eyebrow: "Como trabalhamos",
    heading: "Como apoiamos o seu projeto",
    lead: "Desde a primeira consulta até a coordenação do fornecimento, trabalhamos a partir da sua especificação.",
    steps: [{
      num: "01",
      title: "Definir o requisito",
      desc: "Compartilhe sua aplicação, tipo de malha, abertura ou micragem alvo, material e quantidade."
    }, {
      num: "02",
      title: "Compatibilizar a especificação",
      desc: "Adequamos sua necessidade a produtos de malha tecida, sinterizada, componentes ou arame em cunha sob normas (ISO 9044 / ASTM E2016)."
    }, {
      num: "03",
      title: "Desenvolver peças sob medida",
      desc: "Formatos especiais, dimensões personalizadas, desenhos e componentes OEM quando a linha padrão não atende."
    }, {
      num: "04",
      title: "Cotação e coordenação logística",
      desc: "Proposta comercial por escrito e coordenação de fornecimento para embarques internacionais."
    }],
    primaryBtn: "Solicitar orçamento",
    secondaryBtn: "Engenharia OEM sob medida"
  }
};
const stepIcons = [FileText, CheckCircle2, Cog, Send];
export default function HowWeWork() {
  const {
    lang
  } = useLanguage();
  const current = pick(lang, copy);
  return <section id="howwework" data-nav="light" className="bg-background text-foreground border-b border-border mf-section">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="space-y-4 max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 border border-border bg-muted px-3 py-1 mf-caption font-mono tracking-wider uppercase text-foreground rounded-md w-fit">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span>{current.eyebrow}</span>
            </div>
            <h2 className="mf-h2 text-foreground font-default">
              {current.heading}
            </h2>
            <p className="mf-lead text-muted-foreground">
              {current.lead}
            </p>
          </div>
        </Reveal>

        {/* 4-step Timeline */}
        <div className="relative mb-12">
          {/* Horizontal connection line on desktop */}
          <div aria-hidden="true" className="hidden lg:block absolute top-7 left-12 right-12 h-px bg-border" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {current.steps.map((step, idx) => {
            const Icon = stepIcons[idx] || FileText;
            return <Reveal key={step.num} delay={Math.min(idx * 60, 300)}>
                  <div className="relative flex flex-col rounded-lg border border-border bg-card p-6 h-full mf-hover-lift">
                    {/* Step Number Circle */}
                    <div className="flex items-center justify-between mb-5">
                      <div className="relative z-10 flex size-14 items-center justify-center rounded-full border border-border bg-background text-primary font-mono font-semibold text-lg">
                        {step.num}
                      </div>
                      <div className="flex size-9 items-center justify-center rounded-md bg-muted text-primary border border-border">
                        <Icon className="size-4 text-primary" />
                      </div>
                    </div>

                    <h3 className="mf-h3 text-foreground mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </Reveal>;
          })}
          </div>
        </div>

        {/* Action Buttons */}
        <Reveal delay={200}>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 pt-2">
            <Button asChild size="lg" className="rounded-md">
              <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2 font-medium">
                <span>{current.primaryBtn}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-md border-border bg-card">
              <Link to="/OEMCustomMeshEngineeringPage" className="inline-flex items-center gap-2 font-medium">
                <span>{current.secondaryBtn}</span>
              </Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>;
}